<template>
	<div class="animated fadeIn">
		<b-card>
			<b-tabs v-model="activeTab" active>
				<b-tab title="Planer">
					<template v-slot:title>
						<span>Planer</span>
					</template>
					<div class="mt-3">
						<vue-loading :active.sync="isLoadingCalendarView" :is-full-page="false"/>
						<div class="calendar-header">
							<div class="calendar-header-left">
								<replacement-vehicle-department-filter ref="replacementVehicleDepartmentFilter" @change="change_replacementVehicleDepartmentFilter"/>
							</div>
							<div class="calendar-header-center">
								<h4 class="date-range-title">
									{{ getDateRangeTitle() }}
								</h4>
							</div>
							<div class="calendar-header-right">
								<b-button class="mr-2" variant="outline-primary" @click="jumpToToday">
									Danes
								</b-button>
								<b-button-group class="mr-2">
									<b-button variant="outline-primary" @click="previousMonth">
										<CIcon name="cil-arrow-left"/>
									</b-button>
									<b-button variant="outline-primary" disabled>
										{{ currentMonthName }} {{ currentYear }}
									</b-button>
									<b-button variant="outline-primary" @click="nextMonth">
										<CIcon name="cil-arrow-right"/>
									</b-button>
								</b-button-group>
								<b-button title="Osveži" variant="outline-primary" @click="refreshCalendarView">
									<CIcon name="cil-loop-circular"/>
								</b-button>
							</div>
						</div>
						
						<div class="calendar-table-wrapper">
							<table class="calendar-table">
								<thead>
									<tr>
										<th class="vehicle-column">Vozilo</th>
										<th class="status-column">Status</th>
										<th v-for="day in daysInMonth" :key="day" class="day-column" :class="{ weekend: isWeekend(day), todayClass: today.getDate() === day }">
											<div class="day-header">
												<div class="day-number">{{ day }}</div>
												<div class="day-name">{{ getDayName(day) }}</div>
											</div>
										</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="vehicle in vehicles" :key="vehicle.id" class="vehicle-row">
										<td class="vehicle-cell clickable" @click="click_replacementVehicleEdit(vehicle)">
											<div class="vehicle-info-container">
												<div class="vehicle-details">
													{{ vehicle.registration_number }}
													<br>
													<small>{{ vehicle.model }}</small>
												</div>
												<div class="vehicle-image">
													<replacement-vehicle-image :tableSmall="true" :image="vehicle.image"/>
												</div>
											</div>
										</td>
										<td class="status-cell">
											<b-badge :variant="getReplacementVehicleStatus(vehicle).variant">
												{{ getReplacementVehicleStatus(vehicle).label }}
											</b-badge>
										</td>
										<td v-for="day in daysInMonth" 
											:key="day" 
											class="reservation-cell"
											:class="[getCellClass(vehicle, day), { 'dragging-over': isDraggedOver(vehicle, day) }]"
											:style="[
												getReservationCellStyle(vehicle, day),
												isDraggedOver(vehicle, day) ? { backgroundColor: `${getVehicleColor(vehicle.id)}40` } : {}
											]"
											@mousedown="startDrag(vehicle, day)"
											@mouseover="updateDrag(vehicle, day)"
											@mouseup="endDrag(vehicle, day)"
											@keydown.esc="cancelDrag()"
											:title="getCellTitle(vehicle, day)"
											tabindex="0" >
											<div v-if="isReservationVisible(vehicle, day)" >
													<div class="reservation-info" 
														:style="{ 
															width: getReservationWidth(vehicle, day),
															backgroundColor: getVehicleColor(vehicle.id)
														}">
														<div v-if="false" class="resize-handle left" 
															@mousedown.stop="startResize($event, vehicle, day, 'left')"
															:title="'Spremeni začetni datum'"></div>
														<div class="reservation-content">
															<div class="registration-number">{{ getReservation(vehicle, day).starts_on | viewDate }} - {{ getReservation(vehicle, day).ends_on | viewDate }}</div>
															<div class="client-name" v-if="getReservation(vehicle, day).client">
																{{ mixinGetClientFullName(getReservation(vehicle, day).client) }}
															</div>
														</div>
													<div v-if="false" class="resize-handle right" 
														@mousedown.stop="startResize($event, vehicle, day, 'right')"
														:title="'Spremeni končni datum'"></div>
												</div>
											</div>
											<div v-if="hasSplitReservations(vehicle, day)">
												<div class="reservation-info" 
													:style="{ 
														backgroundColor: 'gray',
														opacity: 0.5
													}">
												</div>
											</div>
											<div v-else-if="today.getDate() === day && isFinishedToday(vehicle, day)">
												<div class="reservation-info">
													<div class="right-half-overlay"></div>
												</div>
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</b-tab>
				<b-tab title="Nadomestna vozila">
					<b-card>
						<vue-loading :active.sync="replacementVehiclesTableProps.isLoading" :is-full-page="false"/>
						<vue-loading :active.sync="isLoadingDeleting" :is-full-page="false"/>
						<div slot="header">
							<div class="card-header-actions">
								<CLink title="Osveži tabelo" href="#" class="card-header-action btn-setting" @click.stop="click_replacementVehiclesTableRefresh">
									<CIcon name="cil-loop-circular" class="mb-1"/>
								</CLink>
								<CLink title="Ponastavi filtre" href="#" class="card-header-action btn-setting" @click.stop="click_replacementVehiclesTableResetFilters">
									<CIcon name="cil-x" class="mb-1"/>
								</CLink>
								<CLink title="Dodaj nadomestno vozilo" href="#" class="card-header-action btn-setting" @click.stop="click_replacementVehicleCreate">
									<CIcon name="cil-plus" class="mb-1"/>
								</CLink>
							</div>
						</div>
						<b-table
							class="replacementVehicleTable"
							ref="replacementVehiclesTable"
							responsive
							show-empty
							hover
							:empty-text="replacementVehiclesTableProps.empty_text"
							:empty-filtered-text="replacementVehiclesTableProps.empty_filtered_text"
							:fields="replacementVehiclesTableFields"
							:current-page="replacementVehiclesTableProps.currentPage"
							:per-page="replacementVehiclesTableProps.itemsPerPage"
							:items="replacementVehiclesTable_items"
							:sort-by.sync="replacementVehiclesTableProps.sortBy"
							:sort-desc.sync="replacementVehiclesTableProps.sortDesc"
							@row-clicked="rowClicked"
							:tbody-tr-class="rowClass">
							<template slot="top-row">
								<td></td>
								<td><b-form-input v-model="replacementVehiclesTableProps.inputFilter.registrationNumber" type="text" @input="replacementVehiclesTableTextFilter_change"/></td>
								<td></td>
								<td></td>
								<td><date-filter-dropdown ref="annualServiceDateFilter" v-model="replacementVehiclesTableProps.inputFilter.annualServiceDate" @input="replacementVehiclesTableFilter_change"/></td>
								<td><date-filter-dropdown ref="registrationDateFilter" v-model="replacementVehiclesTableProps.inputFilter.registrationDate" @input="replacementVehiclesTableFilter_change"/></td>
								<td><b-form-input v-model="replacementVehiclesTableProps.inputFilter.responsiblePerson" type="text" @input="replacementVehiclesTableTextFilter_change"/></td>
								<td><replacement-vehicle-tires-filter ref="replacementVehicleTiresFilter" @change="change_replacementVehicleTiresFilter"/></td>
								<td><replacement-vehicle-department-filter ref="replacementVehicleDepartmentFilter" @change="change_replacementVehicleDepartmentFilter"/></td>
								<td></td>
								<td></td>
								<td></td>
							</template>
							<template v-slot:cell(actions)="data">
								<b-button title="Dodaj rezervacijo" variant="primary" @click.stop="click_replacementVehicleReservationCreate(data.item)" size="sm" class="mr-2">
									<CIcon name="cil-plus"/>
								</b-button>
								<b-button title="Uredi nadomestno vozilo" variant="primary" @click.stop="click_replacementVehicleEdit(data.item)" size="sm" class="mr-2">
									<CIcon name="cil-pencil"/>
								</b-button>
								<b-button title="Izbriši nadomestno vozilo" variant="danger" @click.stop="click_replacementVehicleDelete(data.item)" size="sm" class="mr-2">
									<CIcon name="cil-trash"/>
								</b-button>
							</template>
							<template v-slot:cell(brand)="data">
								<div v-if="data.item.brand">
									<div v-if="data.item.brand.logo" :title="data.item.brand.title" :style="imagesTool.vehicleBrandImage(data.item.brand.logo, 1.8)"></div>
									<CIcon v-else :title="data.item.brand.title" :style="imagesTool.vehicleBrandIcon(3)" name="cil-car-alt"/>
								</div>
							</template>
							<template v-slot:cell(annual_service_date)="data">
								<div v-if="data.item.annual_service_date">
									{{ data.item.annual_service_date | viewDate }}
								</div>
							</template>
							<template v-slot:cell(registration_date)="data">
								<div v-if="data.item.registration_date">
									{{ data.item.registration_date | viewDate }}
								</div>
							</template>
							<template v-slot:cell(workflow_status)="data">
								<div v-if="data.item.workflow_status">
									<b-badge v-if="data.item.workflow_status === 'Z'" title="Zimske" variant="info">
										<CIcon name="cil-snowflake"/>
									</b-badge>
									<b-badge v-else-if="data.item.workflow_status === 'L'" title="Letne" variant="warning">
										<CIcon name="cil-sun"/>
									</b-badge>
								</div>
							</template>
							<template v-slot:cell(vignette)="data">
								<div v-if="data.item.vignette">
									<b-badge title="Avto ima vinjeto" variant="primary">
										<CIcon name="cil-mobile-landscape"/>
									</b-badge>
								</div>
							</template>
							<template v-slot:cell(department)="data">
								<div v-if="data.item.department">
									{{ getReplacementVehicleDepartment(data.item) }}
								</div>
							</template>
							<template v-slot:cell(image)="data">
								<replacement-vehicle-image :tableSmall="true" :image="data.item.image"/>
							</template>
							<template v-slot:cell(status)="data">
								<b-badge :variant="getReplacementVehicleStatus(data.item).variant">{{ getReplacementVehicleStatus(data.item).label }}</b-badge>
								<div v-if="data.item.latest_reservation">
									<div>
										<b-badge title="Komentar" v-if="data.item.latest_reservation.comment" variant="info">
											<CIcon class="mr-1" name="cil-comment-square"/>
											{{ data.item.latest_reservation.comment }}
										</b-badge>
									</div>
									<div class="mt-1">
										<b-badge title="Vozilo oddal" v-if="data.item.latest_reservation.handed_over_by" variant="info">
											<CIcon class="mr-1" name="cil-user"/>
											{{ data.item.latest_reservation.handed_over_by }}
										</b-badge>
									</div>
									<div class="mt-1">
										<b-badge title="Opombe" v-if="data.item.latest_reservation.notes" variant="info">
											<CIcon class="mr-1" name="cil-notes"/>
											{{ data.item.latest_reservation.notes }}
										</b-badge>
									</div>
									<div class="mt-1">
										<b-badge title="Veljavnost najemne pogodbe" v-if="data.item.latest_reservation.contract_valid_from || data.item.latest_reservation.contract_valid_to" variant="info">
											<CIcon class="mr-1" name="cil-file"/>
											<span v-if="data.item.latest_reservation.contract_valid_from">{{ data.item.latest_reservation.contract_valid_from | viewDate }}</span> -
											<span v-if="data.item.latest_reservation.contract_valid_to">{{ data.item.latest_reservation.contract_valid_to | viewDate }}</span>
										</b-badge>
									</div>
								</div>
							</template>
							<template v-slot:cell(own_vehicle)="data">
								{{ data.item.own_vehicle == 1 ? 'Da' : 'Ne' }}
							</template>
						</b-table>
						<nav>
							<b-row>
								<b-col></b-col>
								<b-col cols=2>
									<a class="page-link rows_total float-right">Št. vrstic: {{ replacementVehiclesTableProps.totalItems }}</a>
								</b-col>
							</b-row>
						</nav>
					</b-card>
				</b-tab>
			</b-tabs>
		</b-card>
		<vue-toastr ref="toastr"></vue-toastr>
		<replacement-vehicle-create-edit-modal ref="replacementVehicleCreateEditModal" @emit_replacementVehicleCreatedOrEdited="emit_replacementVehicleCreatedOrEdited"/>
		<replacement-vehicle-reservation-create-edit-modal ref="replacementVehicleReservationCreateEditModal" @emit_replacementVehicleReservationCreatedOrEdited="emit_replacementVehicleReservationCreatedOrEdited"/>
	</div>
</template>

<script>
import settings from '@/settings.js';
import imagesTool from '@/tools/images';
import ReplacementVehicleCreateEdit from '@/views/modals/ReplacementVehicleCreateEdit';
import DateFilterDropdown from '@/views/components/DropdownFilters/DatumFilterDropdown';
import ReplacementVehicleTiresFilter from '@/views/components/DropdownFilters/ReplacementVehicleTiresFilter';
import { REPLACEMENT_VEHICLE_DELETE, REPLACEMENT_VEHICLES_LIST_FILTERED, USER_REPLACEMENT_VEHICLES_DEPARTMENT_FILTER } from '../../store/actions/replacementVehicles';
import { REPLACEMENT_VEHICLE_RESERVATIONS_VEHICLE_RESERVATIONS_FOR_MONTH, REPLACEMENT_VEHICLE_RESERVATION_DELETE } from '../../store/actions/replacementVehicleReservations';
import ReplacementVehicleReservationCreateEdit from '@/views/modals/ReplacementVehicleReservationCreateEdit';
import ReplacementVehicleDepartmentFilter from '@/views/components/DropdownFilters/ReplacementVehicleDepartmentFilter';
import ReplacementVehicleImage from '@/views/components/ReplacementVehicleImage';
import { isSameDay, parseISO, isBefore, startOfDay } from 'date-fns';
import fbApp from "@/firebase";
import { getDatabase, ref, onValue, set } from "firebase/database";

const db = getDatabase(fbApp);
const replacementVehiclesRefresh = ref(db, 'replacementVehicles');
export default {
	name: 'replacement-vehicles',
	components: {
		'replacement-vehicle-create-edit-modal': ReplacementVehicleCreateEdit,
		'date-filter-dropdown': DateFilterDropdown,
		'replacement-vehicle-reservation-create-edit-modal': ReplacementVehicleReservationCreateEdit,
		'replacement-vehicle-tires-filter': ReplacementVehicleTiresFilter,
		'replacement-vehicle-department-filter': ReplacementVehicleDepartmentFilter,
		'replacement-vehicle-image': ReplacementVehicleImage,
	},
	data () {
		return {
			settings,
			imagesTool,
			activeTab: 0,
			currentDate: new Date(),
			today: new Date(),
			replacementVehiclesTableProps: {
				sortDesc: true,
				sortBy: '',
				currentPage: 1,
				itemsPerPage: 100,
				totalItems: 0,
				isLoading: false,
				options_numRowsOnPage: [
					{ value: 50, text: '50' },
					{ value: 100, text: '100' },
					{ value: 200, text: '200' },
					{ value: 1000, text: '1000' }
				],
				empty_text: '',
				empty_filtered_text: 'Ni zadetkov',
				inputFilter: {
					vin: null,
					annualServiceDate: null,
					registrationDate: null,
					responsiblePerson: null,
					ownVehicle: null,
					workflowStatus: null,
					registrationNumber: null,
					typeOfUse: null,
					department: null,
				},
				textFilterTimeout: null,
				errorText: ''
			},
			isLoadingDeleting: false,
			vehicles: [],
			reservations: [],
			isDragging: false,
			dragStart: null,
			dragEnd: null,
			dragVehicle: null,
			isLoadingCalendarView: false,
			isResizing: false,
			resizeDirection: null,
			resizeReservation: null,
			resizeStartDay: null,
			originalStartDate: null,
			originalEndDate: null,
			updateReplacementVehiclesTable: false
		}
	},
	computed: {
		profile() {
			return this.$store.getters.getProfile
		},
		replacementVehiclesTableFields: function () {
			let fields = [];
			fields.push({ key: 'image', label: '', sortable: false, class: 'tdReplacementVehicleImage' });
			fields.push({ key: 'registration_number', label: 'Registrska', sortable: false, class: 'tdReplacementVehicleRegistrationNumber' });
			fields.push({ key: 'brand', label: 'Znamka', sortable: false, class: 'tdReplacementVehicleModel' });
			fields.push({ key: 'model', label: 'Naziv', sortable: false, class: 'tdReplacementVehicleVin' });
			fields.push({ key: 'annual_service_date', label: 'Datum let. servisa', sortable: true, class: 'tdReplacementVehicleDate' });
			fields.push({ key: 'registration_date', label: 'Datum reg.', sortable: true, class: 'tdReplacementVehicleDate' });
			fields.push({ key: 'responsible_person', label: 'Zadolžena oseba', sortable: false, class: 'tdReplacementVehicleResponsiblePerson' });
			fields.push({ key: 'workflow_status', label: 'Gume Z/L', sortable: false, class: 'tdReplacementVehicleTires' });
			fields.push({ key: 'department', label: 'Oddelek', sortable: false, class: '' });
			fields.push({ key: 'status', label: 'Status', sortable: false, class: '' });
			fields.push({ key: 'vignette', label: '', sortable: false, class: 'tdReplacementVehicleVignette' });
			fields.push({ key: 'actions', label: '#', sortable: false, class: 'tdReplacementVehicleActions text-right' })
			return fields;
		},
		currentYear() {
			return this.currentDate.getFullYear();
		},
		currentMonth() {
			return this.currentDate.getMonth();
		},
		currentMonthName() {
			return new Date(this.currentYear, this.currentMonth).toLocaleString('sl-SI', { month: 'long' });
		},
		daysInMonth() {
			return new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
		}
	},
	methods: {
		click_replacementVehiclesTableRefresh: function () {
			this.replacementVehiclesTableProps.itemsPerPage = this.replacementVehiclesTableProps.options_numRowsOnPage[1].value;
			this.$refs.replacementVehiclesTable.refresh();
		},
		click_replacementVehiclesTableResetFilters: function () {
			this.replacementVehiclesTableProps.inputFilter.vin = null;
			this.replacementVehiclesTableProps.inputFilter.annualServiceDate = null;
			this.replacementVehiclesTableProps.inputFilter.registrationDate = null;
			this.replacementVehiclesTableProps.inputFilter.responsiblePerson = null;
			this.replacementVehiclesTableProps.inputFilter.ownVehicle = null;
			this.replacementVehiclesTableProps.inputFilter.workflowStatus = null;
			this.replacementVehiclesTableProps.inputFilter.registrationNumber = null;
			this.replacementVehiclesTableProps.inputFilter.typeOfUse = null;
			this.replacementVehiclesTableProps.inputFilter.department = null;
			this.$refs.annualServiceDateFilter.click_clear();
			this.$refs.registrationDateFilter.click_clear();
			this.$refs.replacementVehicleTiresFilter.click_all();
			this.$refs.replacementVehicleDepartmentFilter.click_all();
			this.click_replacementVehiclesTableRefresh();
		},
		replacementVehiclesTable_items: function (ctx) {
			this.replacementVehiclesTableProps.isLoading = true;
			return this.$store.dispatch(REPLACEMENT_VEHICLES_LIST_FILTERED, { ctx: ctx, tableProps: this.replacementVehiclesTableProps })
				.then((responseData) => {
					this.replacementVehiclesTableProps.isLoading = false;
					this.replacementVehiclesTableProps.items = responseData.replacementVehiclesPagination.data;
					this.replacementVehiclesTableProps.totalItems = responseData.replacementVehiclesPagination.total;
					this.replacementVehiclesTableProps.empty_text = 'Ni vnosov';
					return responseData.replacementVehiclesPagination.data;
				})
				.catch(error => {
					this.replacementVehiclesTableProps.isLoading = false;
					this.replacementVehiclesTableProps.items = [];
					this.replacementVehiclesTableProps.totalItems = 0;
					this.replacementVehiclesTableProps.empty_text = 'Napaka pri pridobivanju nadomestnih vozil';
					console.log(error);
					return [];
				});
		},
		replacementVehiclesTableTextFilter_change: function (value) {
			if (this.replacementVehiclesTableProps.textFilterTimeout !== null) {
				clearTimeout(this.replacementVehiclesTableProps.textFilterTimeout);
			}
			this.replacementVehiclesTableProps.textFilterTimeout = setTimeout(() => {
				this.click_replacementVehiclesTableRefresh();
			}, settings.filterTimeoutLength);
		},
		replacementVehiclesTableFilter_change: function (value) {
			this.click_replacementVehiclesTableRefresh();
		},
		rowClicked (row) {
			/*let modalInput = {
				mode: 3, // details mode
				replacementVehicle: row
			}
			this.$refs.replacementVehicleCreateEditModal.showModal(modalInput);*/
			this.click_replacementVehicleEdit(row);
		},
		click_replacementVehicleCreate: function () {
			let modalInput = {
				mode: 1, // normal mode
				replacementVehicle: null
			}
			this.$refs.replacementVehicleCreateEditModal.showModal(modalInput);
		},
		click_replacementVehicleEdit: function (data) {
			this.showEditReplacementVehicleModal(data);
		},
		showEditReplacementVehicleModal: function (data) {
			let modalInput = {
				mode: 2, // normal mode
				replacementVehicle: data
			}
			this.$refs.replacementVehicleCreateEditModal.showModal(modalInput);
		},
		emit_replacementVehicleCreatedOrEdited: function (data) {
			if (data && data.message == 'edited') this.$refs.toastr.s('Nadomestno vozilo #' + data.replacementVehicle.vin + ' uspešno urejeno.');
			this.$refs.replacementVehiclesTable.refresh();
			this.refreshCalendarView();
		},
		click_replacementVehicleDelete: function (data) {
			this.$bvModal.msgBoxConfirm('Želite izbrisati nadomestno vozilo ' + data.vin + '?', {
				title: 'Potrdite brisanje nadomestnega vozila',
				size: 'sm',
				buttonSize: 'sm',
				okVariant: 'danger',
				okTitle: 'DA',
				cancelTitle: 'NE',
				footerClass: 'p-2',
				hideHeaderClose: false,
				centered: true
			}).then(value => {
				if (value === true) {
					this.isLoadingDeleting = true;
					this.$store.dispatch(REPLACEMENT_VEHICLE_DELETE, { id: data.id })
						.then((responseData) => {
							this.isLoadingDeleting = false;
							this.$refs.toastr.s('Nadomestno vozilo #' + data.vin + ' uspešno izbrisano.');
							this.$refs.replacementVehiclesTable.refresh();
						})
						.catch(error => {
							this.isLoadingDeleting = false;
							console.error(error);
							if(error.response && error.response.data && error.response.data.code) {
								this.$refs.toastr.e('Napaka pri brisanju nadomestnega vozila! (' + error.response.data.code + ')');
							} else {
								this.$refs.toastr.e('Napaka pri brisanju nadomestnega vozila! Prosimo poizkusite kasneje.');
							}
						});
				}
			}).catch(err => {
				console.error(err);
			})
		},
		click_replacementVehicleReservationCreate: function (row) {
			let replacementVehicleReservation = { replacementVehicleId: row.id};
			let modalInput = {
				mode: 1, // create
				replacementVehicleReservation,
				replacementVehicleVin: row.vin,
				issuedButNotFinishedReservation: row.issued_but_not_finished_reservation
			}
			this.$refs.replacementVehicleReservationCreateEditModal.showModal(modalInput);
		},
		getReplacementVehicleStatus: function(item) {
			if (!item.todays_reservation) return { variant: 'success', label: 'Prosto', class: 'table-success'};
			if (item.todays_reservation.contract_finished_at) return { variant: 'success', label: 'Prosto', class: 'table-success'};
			let notAvailableVariant = 'danger';
			if (!item.todays_reservation.contract_printed_at) notAvailableVariant = 'warning';
			return { variant: notAvailableVariant, label: this.$options.filters.viewDate(item.todays_reservation.starts_on) + '-' + this.$options.filters.viewDate(item.todays_reservation.ends_on), class: 'table-danger'};
		},
		getReplacementVehicleDepartment: function(item) {
			const department = this.settings.replacementVehicleDepartmentOptions.find(department => department.value === item.department);
			if (department) return department.label;
			return '/';
		},
		rowClass: function(item) {
			if (item) return this.getReplacementVehicleStatus(item).class;
		},
		change_replacementVehicleTiresFilter: function (selectedItems) {
			if (selectedItems && selectedItems.length > 0) {
				this.replacementVehiclesTableProps.inputFilter.workflowStatus = selectedItems[0].value;
			} else {
				this.replacementVehiclesTableProps.inputFilter.workflowStatus = null;
			}
			this.$refs.replacementVehiclesTable.refresh();
		},
		change_replacementVehicleDepartmentFilter: function (selectedItems) {
			if (selectedItems && selectedItems.length > 0) {
				this.replacementVehiclesTableProps.inputFilter.department = selectedItems[0].value;
				this.$store.dispatch(USER_REPLACEMENT_VEHICLES_DEPARTMENT_FILTER, this.replacementVehiclesTableProps.inputFilter.department)
					.then((responseData) => {
					})
					.catch(error => {
						console.error(error);
				});
			} else {
				this.$store.dispatch(USER_REPLACEMENT_VEHICLES_DEPARTMENT_FILTER, null)
					.then((responseData) => {
					})
					.catch(error => {
						console.error(error);
				});
				this.replacementVehiclesTableProps.inputFilter.department = null;
			}
			this.$refs.replacementVehiclesTable.refresh();
			this.refreshCalendarView();
		},
		click_reservationEdit: function (replacementVehicle, item) {
			let modalInput = {
				mode: 2, // edit mode
				replacementVehicleReservation: item,
				replacementVehicleVin: replacementVehicle.vin,
				issuedButNotFinishedReservation: replacementVehicle.issued_but_not_finished_reservation
			}
			this.$refs.replacementVehicleReservationCreateEditModal.showModal(modalInput);
		},
		previousMonth() {
			this.currentDate = new Date(this.currentYear, this.currentMonth - 1);
			this.refreshCalendarView();
		},
		nextMonth() {
			this.currentDate = new Date(this.currentYear, this.currentMonth + 1);
			this.refreshCalendarView();
		},
		jumpToToday() {
			this.currentDate = new Date();
			this.refreshCalendarView();
		},
		refreshCalendarView() {
			this.isLoadingCalendarView = true;
			
			// Get reservations for the current month
			this.$store.dispatch(REPLACEMENT_VEHICLE_RESERVATIONS_VEHICLE_RESERVATIONS_FOR_MONTH, {
				year: this.currentYear,
				month: this.currentMonth + 1, // JavaScript months are 0-based, but our API expects 1-based
				departmentFilter: this.replacementVehiclesTableProps.inputFilter.department
			})
			.then((response) => {
				// Ensure we have an array of vehicles
				const vehiclesData = Array.isArray(response.vehiclesWithReservations) ? response.vehiclesWithReservations : [];
				this.vehicles = vehiclesData;
				
				// Process reservations from all vehicles
				const allReservations = [];
				vehiclesData.forEach(vehicle => {
					if (vehicle.month_reservations && Array.isArray(vehicle.month_reservations)) {
						vehicle.month_reservations.forEach(reservation => {
							allReservations.push({
								...reservation,
								replacement_vehicle_id: vehicle.id
							});
						});
					}
				});
				
				this.reservations = allReservations;
				this.isLoadingCalendarView = false;
			})
			.catch(error => {
				console.error(error);
				this.isLoadingCalendarView = false;
				this.$refs.toastr.e('Napaka pri pridobivanju podatkov');
			});
		},
		getReservation(vehicle, day) {
			const date = new Date(this.currentYear, this.currentMonth, day);
			
			const dateStr = date.toISOString().split('T')[0];
			
			const reservation = this.reservations.find(reservation => {
				const startDate = new Date(reservation.starts_on);
				const endDate = new Date(reservation.ends_on);
				const compareDate = date.getTime();
				const compareStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
				const compareEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
				
				return reservation.replacement_vehicle_id === vehicle.id &&
					compareDate >= compareStart &&
					compareDate <= compareEnd;
			});
			
			return reservation;
		},
		isReservationStart(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (!reservation) return false;
			
			const date = new Date(this.currentYear, this.currentMonth, day);
			const startDate = new Date(reservation.starts_on);
			return date.getFullYear() === startDate.getFullYear() &&
				   date.getMonth() === startDate.getMonth() &&
				   date.getDate() === startDate.getDate();
		},
		getReservationWidth(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (!reservation) return '100%';
			
			const startDate = new Date(reservation.starts_on);
			const endDate = new Date(reservation.ends_on);
			const monthStart = new Date(this.currentYear, this.currentMonth, 1);
			const monthEnd = new Date(this.currentYear, this.currentMonth + 1, 0);
			
			// Adjust dates if reservation starts before month or ends after month
			const effectiveStart = startDate < monthStart ? monthStart : startDate;
			const effectiveEnd = endDate > monthEnd ? monthEnd : endDate;
			
			// Add 1 to include both start and end days
			const days = Math.floor((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
			
			// Calculate width based on number of days
			return `calc(${days * 100}% + ${(days - 1)}px)`;
		},
		getCellClass(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			const isStart = this.isReservationStart(vehicle, day);
			return {
				'has-reservation': !!reservation,
				'reservation-start': isStart,
				'reservation-middle': !!reservation && !isStart,
				'weekend': this.isWeekend(day),
				'todayClass': day === this.today.getDate()
			};
		},
		getCellTitle(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (!reservation) return '';
			const dateLabel = this.$options.filters.viewDate(reservation.starts_on) + ' - ' + this.$options.filters.viewDate(reservation.ends_on);
			const clientTitle = reservation.client ? this.mixinGetClientFullName(reservation.client) : '/';
			return dateLabel + '\n' + clientTitle;
		},
		isWeekend(day) {
			const date = new Date(this.currentYear, this.currentMonth, day);
			const dayOfWeek = date.getDay();
			return dayOfWeek === 0 || dayOfWeek === 6;
		},
		startDrag(vehicle, day) {
			const existingReservation = this.getReservation(vehicle, day);
			if (!existingReservation || (existingReservation.contract_finished_at && new Date(existingReservation.contract_finished_at).getDate() === day)) {
				this.isDragging = true;
				this.dragStart = day;
				this.dragEnd = day;
				this.dragVehicle = vehicle;
			} else {
				this.click_reservationEdit(vehicle, existingReservation);
			}
		},
		updateDrag(vehicle, day) {
			if (this.isDragging && vehicle.id === this.dragVehicle.id) {
				this.dragEnd = day;
			}
		},
		endDrag(vehicle, day) {
			if (this.isDragging && vehicle.id === this.dragVehicle.id) {
				const startDay = Math.min(this.dragStart, this.dragEnd);
				const endDay = Math.max(this.dragStart, this.dragEnd);
				
				// Create reservation with the selected date range
				const startDate = new Date(this.currentYear, this.currentMonth, startDay+1);
				const endDate = new Date(this.currentYear, this.currentMonth, endDay+1);
				startDate.setUTCHours(0,0,0,0);
				endDate.setUTCHours(0,0,0,0);
				if (isBefore(startOfDay(startDate), startOfDay(new Date()))) {
					this.resetDragState();
					return;
				} 
				const replacementVehicleReservation = {
					replacementVehicleId: vehicle.id,
					startsOn: startDate
				};
				if (startDay != endDay) replacementVehicleReservation.endsOn = endDate;
				let modalInput = {
					mode: 1, // create mode
					replacementVehicleReservation,
					replacementVehicleVin: vehicle.vin,
					issuedButNotFinishedReservation: vehicle.issued_but_not_finished_reservation
				};
				this.$refs.replacementVehicleReservationCreateEditModal.showModal(modalInput);
				this.resetDragState();
			}
		},
		resetDragState() {
			// Reset drag state
			this.dragStart = null;
			this.dragEnd = null;
			this.dragVehicle = null;
			this.isDragging = false;
		},
		cancelDrag() {
			this.resetDragState();
		},
		isDraggedOver(vehicle, day) {
			if (!this.isDragging || vehicle.id !== this.dragVehicle?.id) return false;
			
			const start = Math.min(this.dragStart, this.dragEnd);
			const end = Math.max(this.dragStart, this.dragEnd);
			return day >= start && day <= end;
		},
		getDayName(day) {
			const date = new Date(this.currentYear, this.currentMonth, day);
			return date.toLocaleDateString('sl-SI', { weekday: 'short' });
		},
		getDateRangeTitle() {
			const firstDay = new Date(this.currentYear, this.currentMonth, 1);
			const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
			return `${firstDay.getDate()} ${firstDay.toLocaleDateString('sl-SI', { month: 'long' })} ${firstDay.getFullYear()} - ${lastDay.getDate()} ${lastDay.toLocaleDateString('sl-SI', { month: 'long' })} ${lastDay.getFullYear()}`;
		},
		getVehicleColor(vehicleId) {
			const colorIndex = vehicleId % this.settings.reservationColors.length;
			return this.settings.reservationColors[colorIndex];
		},
		getReservationCellStyle(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (reservation && !this.isReservationStart(vehicle, day)) {
				const color = this.getVehicleColor(vehicle.id);
				return {
					backgroundColor: color,
					borderLeft: 'none',
					borderRight: 'none'
				};
			}
			return {};
		},
		startResize(event, vehicle, day, direction) {
			const reservation = this.getReservation(vehicle, day);
			if (!reservation) return;
			
			event.stopPropagation();
			this.isResizing = true;
			this.resizeDirection = direction;
			this.resizeReservation = reservation;
			this.resizeStartDay = day;
			this.originalStartDate = new Date(reservation.starts_on);
			this.originalEndDate = new Date(reservation.ends_on);
			
			document.addEventListener('mousemove', this.handleResize);
			document.addEventListener('mouseup', this.endResize);
		},
		handleResize(event) {
			if (!this.isResizing) return;
			
			const calendar = document.querySelector('.calendar-table');
			const rect = calendar.getBoundingClientRect();
			const dayWidth = rect.width / this.daysInMonth;
			const dayOffset = Math.round((event.clientX - rect.left - rect.width / this.daysInMonth) / dayWidth) + 1;
			
			const newDay = Math.max(1, Math.min(this.daysInMonth, dayOffset));
			const newDate = new Date(this.currentYear, this.currentMonth, newDay);
			
			if (this.resizeDirection === 'left') {
				if (newDate <= this.originalEndDate) {
					this.resizeReservation.starts_on = newDate.toISOString().split('T')[0];
				}
			} else {
				if (newDate >= this.originalStartDate) {
					this.resizeReservation.ends_on = newDate.toISOString().split('T')[0];
				}
			}
		},
		endResize(event) {
			/*if (!this.isResizing) return;
			
			document.removeEventListener('mousemove', this.handleResize);
			document.removeEventListener('mouseup', this.endResize);
			
			const modalInput = {
				mode: 2, // edit mode
				replacementVehicleReservation: this.resizeReservation,
				replacementVehicleVin: this.resizeReservation.replacement_vehicle.vin,
			};
			this.$refs.replacementVehicleReservationCreateEditModal.showModal(modalInput);
			
			this.isResizing = false;
			this.resizeDirection = null;
			this.resizeReservation = null;
			this.resizeStartDay = null;
			this.originalStartDate = null;
			this.originalEndDate = null;*/
		},
		emit_replacementVehicleReservationCreatedOrEdited: function (data) {
			this.refreshCalendarView();
			this.updateReplacementVehiclesTable = false;
			set(replacementVehiclesRefresh, {
				updated_on: new Date().getTime()
			});
		},
		isReservationVisible(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (!reservation) return false;
			if (this.isReservationStart(vehicle, day)) return true;
			if (day === 1) {
				const startDate = new Date(reservation.starts_on);
				const firstOfMonth = new Date(this.currentYear, this.currentMonth, 1);
				return startDate < firstOfMonth;
			}
			return false;
		},
		getOverlapingReservationsForDay(vehicle, day) {
			const date = new Date(this.currentYear, this.currentMonth, day);
			const compareDate = date.setHours(0,0,0,0);
			return this.reservations.filter(reservation => {
				const startDate = new Date(reservation.starts_on).setHours(0,0,0,0);
				const endDate = new Date(reservation.ends_on).setHours(0,0,0,0);
				return reservation.replacement_vehicle_id === vehicle.id &&
					compareDate >= startDate &&
					compareDate <= endDate;
			});
		},
		hasSplitReservations(vehicle, day) {
			if (this.getOverlapingReservationsForDay(vehicle, day).length > 1) {
				return true;
			}
			return false;
		},
		isFinishedToday(vehicle, day) {
			const reservation = this.getReservation(vehicle, day);
			if (reservation && isSameDay(parseISO(reservation.contract_finished_at), this.today)) {
				return true;
			}
			return false;
		}
	},
	created: function () {
		let $main = this;
		onValue(replacementVehiclesRefresh, (snapshot) => {
			if ($main.updateReplacementVehiclesTable) {
				$main.$refs.replacementVehiclesTable.refresh();
				$main.refreshCalendarView();
				$main.$refs.toastr.s('Podatki nadomestnih vozil posodobljeni');
			}
			$main.updateReplacementVehiclesTable = true;
		});
	},
	mounted() {
		if (this.$store.getters.getUserReplacementVehiclesDepartmentFilter) {
			this.replacementVehiclesTableProps.inputFilter.department = this.$store.getters.getUserReplacementVehiclesDepartmentFilter;
		}
		this.refreshCalendarView();
	},
	watch: {
		activeTab: {
			handler(newTab) {
				// Tab index 1 is the Planer tab
				if (newTab === 0) {
					this.refreshCalendarView();
				}
			}
		}
	}
}
</script>
<style scoped>
.table-responsive-sm {
	margin-bottom: 0;
}
.pagination {
	margin-left: 1rem;
	margin-right: 1rem;
}
.page-link.rows_total {
	text-align: center;
	border-radius: 5px;
	width: 120px;
}
</style>
<style>
.tdReplacementVehicleVin {
	width: 13rem;
	min-width: 13rem;
}
.tdReplacementVehicleModel {
	width: 5rem;
	min-width: 5cap;
}
.tdReplacementVehicleDate {
	width: 9rem;
	min-width: 9rem;
}
.tdReplacementVehicleResponsiblePerson {
	width: 11rem;
	min-width: 11rem;
}
.tdReplacementVehicleOwnVehicle {
	width: 8rem;
	min-width: 8rem;
}
.tdReplacementVehicleRegistrationNumber {
	width: 9rem;
	min-width: 9rem;
}
.tdReplacementVehicleTires {
	width: 6rem;
	min-width: 6rem;
	max-width: 6rem;
}
.tdReplacementVehicleVignette {
	width: 2rem;
	min-width: 2rem;
	max-width: 2rem;
}
.tdReplacementVehicleImage {
	width: 2rem;
	min-width: 2rem;
	max-width: 2rem;
}
.tdReplacementVehicleActions {
	width: 11rem;
	min-width: 11rem;
}
.replacementVehicleTable {
	min-height: 500px;
}
.table thead th[aria-sort="descending"] {
    cursor: pointer;
}
.table thead th[aria-sort="descending"] div:after {
	content:'↓';
	margin-left: 0.5em;
}
.table thead th[aria-sort="ascending"] {
    cursor: pointer;
}
.table thead th[aria-sort="ascending"] div:after {
	content:'↑';
	margin-left: 0.5em;
}
.table thead th[aria-sort="none"] {
    cursor: pointer;
}
.calendar-table-wrapper {
	overflow-x: auto;
}

.calendar-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}

.calendar-table th,
.calendar-table td {
	border: 1px solid #dee2e6;
	padding: 4px;
	text-align: center;
}

.vehicle-column {
	width: 150px;
	position: sticky;
	left: 0;
	background: white;
	z-index: 1;
}

.status-column {
	width: 150px;
	position: sticky;
	left: 150px;
	background: white;
	z-index: 1;
}

.day-column {
	min-width: 100px;
	padding: 0 !important;
}

.day-column.weekend {
	background-color: #f8f9fa;
}

.day-column.todayClass {
	background-color: #c1d751;
}

.vehicle-cell {
	position: sticky;
	left: 0;
	background: white;
	z-index: 1;
	text-align: left !important;
	font-weight: bold;
}

.status-cell {
	position: sticky;
	left: 150px;
	background: white;
	z-index: 1;
	text-align: center !important;
}

.reservation-cell {
	height: 60px;
	cursor: pointer;
	transition: background-color 0.2s;
	padding: 0;
	position: relative;
	user-select: none;
	border: 1px solid #dee2e6;
}

.reservation-cell.weekend {
	background-color: #f8f9fa;
}

.reservation-cell.todayClass {
	background-color: #c1d751;
}

.reservation-cell.has-reservation {
	background-color: transparent;
}

.reservation-cell.reservation-middle {
	padding: 0;
}

.reservation-info {
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	color: white;
	z-index: 1;
	padding: 4px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	border-radius: 0;
	font-weight: bold;
}

.right-half-overlay {
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background-color: white;
  opacity: 1;
}

.reservation-content {
	flex: 1;
	min-width: 0;
	padding: 0 8px;
}

.resize-handle {
	width: 8px;
	height: 100%;
	cursor: col-resize;
	position: absolute;
	top: 0;
	background-color: rgba(0, 0, 0, 0.1);
	transition: background-color 0.2s;
}

.resize-handle:hover {
	background-color: rgba(0, 0, 0, 0.2);
}

.resize-handle.left {
	left: 0;
}

.resize-handle.right {
	right: 0;
}

.calendar-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;
	padding: 0.5rem;
	background-color: #fff;
	border-radius: 4px;
}

.calendar-header-left,
.calendar-header-right {
	flex: 1;
	display: flex;
	align-items: center;
}

.calendar-header-center {
	flex: 2;
	text-align: center;
}

.calendar-header-right {
	justify-content: flex-end;
}

.date-range-title {
	margin: 0;
	font-weight: 500;
	color: #495057;
}

.btn-refresh {
	padding: 0.375rem 0.75rem;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	color: #6c757d;
	transition: all 0.2s;
}

.btn-refresh:hover {
	background-color: #f8f9fa;
	color: #495057;
	text-decoration: none;
}

.dragging-over {
	cursor: pointer !important;
}

.day-header {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 4px;
}

.day-number {
	font-size: 1.1em;
	font-weight: bold;
}

.day-name {
	font-size: 0.8em;
	color: #666;
}

.month-header {
	font-size: 1.2em;
	font-weight: bold;
	text-align: center;
	padding: 8px;
	background-color: #f8f9fa;
	border-bottom: 2px solid #dee2e6;
}

.calendar-header-right .btn-group {
	min-width: 200px;
}

.calendar-header-right .btn-group button:nth-child(2) {
	min-width: 140px;
}

.vehicle-info-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.vehicle-details {
	flex: 1;
	text-align: left;
	padding-right: 8px;
}

.vehicle-image {
	flex-shrink: 0;
	display: flex;
	align-items: center;
}
</style>